import { useEffect, useMemo, useRef } from "react";
import moment from "moment";
import "./CustomTimePicker.scss";

const HOURS = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES = Array.from({ length: 60 }, (_, i) => i);
const SECONDS = Array.from({ length: 60 }, (_, i) => i);
const WHEEL_ITEM_HEIGHT = 40;

const parseTime = (value, includeSeconds) => {
    const formats = includeSeconds
        ? ["HH:mm:ss", "HH:mm", "H:mm"]
        : ["HH:mm", "H:mm"];
    const parsed = moment(value, formats, true);
    if (parsed.isValid()) {
        return parsed;
    }
    return moment();
};

const CustomTimePicker = ({ value, onChange, className = "", includeSeconds = false }) => {
    const time = useMemo(() => parseTime(value, includeSeconds), [value, includeSeconds]);
    const hour12 = Number(time.format("h"));
    const minute = Number(time.format("m"));
    const second = Number(time.format("s"));
    const meridiem = time.format("A");

    const minuteScrollRef = useRef(null);
    const secondScrollRef = useRef(null);
    const scrollSyncLockRef = useRef(false);
    const minuteScrollEndTimerRef = useRef(null);
    const secondScrollEndTimerRef = useRef(null);

    const emitChange = (nextHour12, nextMinute, nextMeridiem, nextSecond = second) => {
        const hour24 =
            nextMeridiem === "PM"
                ? (nextHour12 === 12 ? 12 : nextHour12 + 12)
                : (nextHour12 === 12 ? 0 : nextHour12);

        const nextMoment = moment()
            .hour(hour24)
            .minute(nextMinute)
            .second(includeSeconds ? nextSecond : 0)
            .millisecond(0);

        onChange(nextMoment.format(includeSeconds ? "HH:mm:ss" : "HH:mm"));
    };

    const getWheelItemHeight = (scrollEl) => {
        const option = scrollEl?.querySelector("button");
        const height = option?.getBoundingClientRect().height;
        return height > 0 ? height : WHEEL_ITEM_HEIGHT;
    };

    const scrollWheelToIndex = (ref, index, behavior = "auto") => {
        const el = ref.current;
        if (!el) return;
        scrollSyncLockRef.current = true;
        el.scrollTo({ top: index * getWheelItemHeight(el), behavior });
        window.setTimeout(() => {
            scrollSyncLockRef.current = false;
        }, behavior === "smooth" ? 300 : 0);
    };

    useEffect(() => {
        scrollWheelToIndex(minuteScrollRef, minute);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [minute, value]);

    useEffect(() => {
        if (!includeSeconds) return;
        scrollWheelToIndex(secondScrollRef, second);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [second, value, includeSeconds]);

    const handleMinuteScroll = () => {
        if (scrollSyncLockRef.current) return;

        if (minuteScrollEndTimerRef.current) {
            window.clearTimeout(minuteScrollEndTimerRef.current);
        }

        minuteScrollEndTimerRef.current = window.setTimeout(() => {
            const el = minuteScrollRef.current;
            if (!el) return;

            const index = Math.round(el.scrollTop / getWheelItemHeight(el));
            const nextMinute = Math.min(59, Math.max(0, index));

            if (nextMinute !== minute) {
                emitChange(hour12, nextMinute, meridiem);
            } else {
                scrollWheelToIndex(minuteScrollRef, nextMinute);
            }
        }, 80);
    };

    const handleSecondScroll = () => {
        if (scrollSyncLockRef.current) return;

        if (secondScrollEndTimerRef.current) {
            window.clearTimeout(secondScrollEndTimerRef.current);
        }

        secondScrollEndTimerRef.current = window.setTimeout(() => {
            const el = secondScrollRef.current;
            if (!el) return;

            const index = Math.round(el.scrollTop / getWheelItemHeight(el));
            const nextSecond = Math.min(59, Math.max(0, index));

            if (nextSecond !== second) {
                emitChange(hour12, minute, meridiem, nextSecond);
            } else {
                scrollWheelToIndex(secondScrollRef, nextSecond);
            }
        }, 80);
    };

    const clockPositions = useMemo(
        () =>
            HOURS.map((hour, index) => {
                const angle = (index * 30 - 90) * (Math.PI / 180);
                const radius = 42;
                return {
                    hour,
                    x: 50 + radius * Math.cos(angle),
                    y: 50 + radius * Math.sin(angle),
                };
            }),
        []
    );

    const rootClassName = [
        "custom-time-picker",
        includeSeconds && "custom-time-picker--with-seconds",
        className,
    ]
        .filter(Boolean)
        .join(" ");

    return (
        <div className={rootClassName} role="dialog" aria-label="Choose time">
            <div className="custom-time-picker__display">
                {time.format("HH")} : {time.format("mm")}
                {includeSeconds && <> : {time.format("ss")}</>}
                {" "}
                {meridiem}
            </div>

            <div className="custom-time-picker__meridiem">
                {["AM", "PM"].map((part) => (
                    <button
                        key={part}
                        type="button"
                        className={`custom-time-picker__meridiem-btn${
                            meridiem === part ? " custom-time-picker__meridiem-btn--active" : ""
                        }`}
                        onClick={() => emitChange(hour12, minute, part)}
                    >
                        {part}
                    </button>
                ))}
            </div>

            <div className="custom-time-picker__clock-row">
                <div className="custom-time-picker__clock">
                    <div className="custom-time-picker__clock-face">
                        {clockPositions.map(({ hour, x, y }) => (
                            <button
                                key={hour}
                                type="button"
                                className={`custom-time-picker__hour${
                                    hour12 === hour ? " custom-time-picker__hour--active" : ""
                                }`}
                                style={{ left: `${x}%`, top: `${y}%` }}
                                onClick={() => emitChange(hour, minute, meridiem)}
                            >
                                {hour}
                            </button>
                        ))}
                        <div
                            className="custom-time-picker__hand custom-time-picker__hand--hour"
                            style={{ transform: `rotate(${(hour12 % 12) * 30 + minute * 0.5}deg)` }}
                        />
                        <div
                            className="custom-time-picker__hand custom-time-picker__hand--minute"
                            style={{ transform: `rotate(${minute * 6}deg)` }}
                        />
                        <div className="custom-time-picker__center-dot" />
                    </div>
                </div>

                <div className="custom-time-picker__scroll-wheels">
                    <div className="custom-time-picker__minutes">
                        <span className="custom-time-picker__minutes-label">Minute</span>
                        <div className="custom-time-picker__minute-wheel">
                            <div className="custom-time-picker__minute-wheel-highlight" aria-hidden="true" />
                            <div
                                ref={minuteScrollRef}
                                className="custom-time-picker__minute-scroll"
                                onScroll={handleMinuteScroll}
                                role="listbox"
                                aria-label="Minute"
                                aria-valuenow={minute}
                                aria-valuemin={0}
                                aria-valuemax={59}
                            >
                                <div className="custom-time-picker__minute-scroll-spacer" aria-hidden="true" />
                                {MINUTES.map((min) => (
                                    <button
                                        key={min}
                                        type="button"
                                        role="option"
                                        aria-selected={minute === min}
                                        className={`custom-time-picker__minute-option${
                                            minute === min ? " custom-time-picker__minute-option--active" : ""
                                        }`}
                                        onClick={() => {
                                            emitChange(hour12, min, meridiem);
                                            scrollWheelToIndex(minuteScrollRef, min, "smooth");
                                        }}
                                    >
                                        {String(min).padStart(2, "0")}
                                    </button>
                                ))}
                                <div className="custom-time-picker__minute-scroll-spacer" aria-hidden="true" />
                            </div>
                        </div>
                    </div>

                    {includeSeconds && (
                        <div className="custom-time-picker__seconds">
                            <span className="custom-time-picker__seconds-label">Second</span>
                            <div className="custom-time-picker__second-wheel">
                                <div className="custom-time-picker__second-wheel-highlight" aria-hidden="true" />
                                <div
                                    ref={secondScrollRef}
                                    className="custom-time-picker__second-scroll"
                                    onScroll={handleSecondScroll}
                                    role="listbox"
                                    aria-label="Second"
                                    aria-valuenow={second}
                                    aria-valuemin={0}
                                    aria-valuemax={59}
                                >
                                    <div className="custom-time-picker__second-scroll-spacer" aria-hidden="true" />
                                    {SECONDS.map((sec) => (
                                        <button
                                            key={sec}
                                            type="button"
                                            role="option"
                                            aria-selected={second === sec}
                                            className={`custom-time-picker__second-option${
                                                second === sec ? " custom-time-picker__second-option--active" : ""
                                            }`}
                                            onClick={() => {
                                                emitChange(hour12, minute, meridiem, sec);
                                                scrollWheelToIndex(secondScrollRef, sec, "smooth");
                                            }}
                                        >
                                            {String(sec).padStart(2, "0")}
                                        </button>
                                    ))}
                                    <div className="custom-time-picker__second-scroll-spacer" aria-hidden="true" />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CustomTimePicker;
